import { msgData, requestApp, WEB_TO_APP } from "common/webview";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import { deviceAtom } from "store/deviceStore";

interface UseWebviewOptions {
    onMessage?: (type: string, data: any) => void;
}

export const useWebview = (options?: UseWebviewOptions) => {
    const setDevice = useSetAtom(deviceAtom);
    const device = useAtomValue(deviceAtom);
    const readySent = useRef(false);
    const fontLoaded = useRef(false);
    const themeApplied = useRef(false);

    if (window.ReactNativeWebView) {
        console.log = function (...props) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'LOGS', data: props }));
        };
        console.error = function (...props) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'ERROR_LOGS', data: props }));
        };
    }

    const handleMessage = (e: MessageEvent) => {
        const { type, data } = msgData(e);
        if (!type) return;
        switch (type) {
            default:
                options?.onMessage?.(type, data);
                break;
        }
    };

    useEffect(() => {
        // injectedJS로 이미 주입된 device 정보를 atom에 저장
        if (window.__DEVICE__) {
            const d = window.__DEVICE__;
            setDevice({
                model: d.model,
                os: d.os,
                isDarkMode: d.isDarkMode,
                language: d.language || 'ko',
                deviceId: d.deviceId || '',
                countryCd: d.countryCd || '',
                inset: d.inset,
            });
        }

        if(window.__APP__?.primaryColor) {
            const color = window.__APP__?.primaryColor;
            document.documentElement.style.setProperty("--primary", color);
        }

        if (window.__DEVICE__?.inset?.navigationBarHeight) {
            var safeBottom = window.__DEVICE__?.inset?.navigationBarHeight + "px";
            document.documentElement.style.setProperty("--safe-bottom", safeBottom);
        }

        // iframe 진입(예: 모이 설정 모달) 도 부모창에서 SEND_TOKEN 등 postMessage 를 받아야 함
        const isIframe = typeof window !== 'undefined' && window.parent !== window;
        if (window.ReactNativeWebView || isIframe) {
            window.addEventListener('message', handleMessage);
            (document as any).addEventListener('message', handleMessage);
        }

        // iframe 환경: 부모창에 mount 완료 신호 → 부모가 SEND_TOKEN 응답 (load race 회피)
        if (isIframe) {
            try {
                window.parent.postMessage(JSON.stringify({ type: 'IFRAME_READY', data: {} }), '*');
            } catch {
                // 무시
            }
        }

        return () => {
            if (window.ReactNativeWebView || isIframe) {
                window.removeEventListener('message', handleMessage);
                (document as any).removeEventListener('message', handleMessage);
            }
        };
    }, []);

    const sendReady = () => {
        if (readySent.current) return;
        if (!fontLoaded.current || !themeApplied.current) return;
        readySent.current = true;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                requestApp(WEB_TO_APP.MSG_READY);
            });
        });
    };

    // 폰트 로딩
    useEffect(() => {
        document.fonts.load('1rem "Pretendard JP"').then(() => {
            fontLoaded.current = true;
            sendReady();
        });
        // 폰트 CDN 장애 대비 타임아웃
        setTimeout(() => {
            fontLoaded.current = true;
            sendReady();
        }, 3000);
    }, []);

    // 테마 적용
    useEffect(() => {
        // native가 아닌 환경(web/none)에서는 디바이스 정보가 오지 않으므로 테마 대기 없이 바로 ready
        if (!window.ReactNativeWebView) {
            themeApplied.current = true;
            sendReady();
            return;
        }

        if (device.isDarkMode === undefined) return;

        if (device.isDarkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }

        themeApplied.current = true;
        sendReady();
    }, [device.isDarkMode]);
};
